import { db } from "../firebase.config";
import { 
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  deleteDoc,
  writeBatch
} from "firebase/firestore";

export const ThreadService = {
  // Create a new thread
  async createThread(userId, firstMessage, hasImage = false, imageName = null) {
    try {
      // Get first few words (up to 20 characters) and add ellipsis
      const title = firstMessage.length > 20 
        ? firstMessage.substring(0, 20).trim() + "..."
        : firstMessage;

      // Create thread with initial message - make sure hasImage is explicitly set
      const threadRef = await addDoc(collection(db, "threads"), {
        userId,
        title,
        createdAt: new Date(),
        lastUpdated: new Date(),
        messages: [{
          role: "user",
          content: firstMessage,
          timestamp: new Date(),
          hasImage: Boolean(hasImage), // Ensure it's a boolean
          ...(hasImage && { imageName: imageName }) // Only add imageName if hasImage is true
        }]
      });
      return threadRef.id;
    } catch (error) {
      console.error("Error creating thread:", error);
      throw error;
    }
  },

  // Create a new thread with system prompt
  async createThreadWithSystem(userId, title, systemPrompt, userPrompt) {
    try {
      const threadRef = await addDoc(collection(db, "threads"), {
        userId,
        title,
        createdAt: new Date(),
        lastUpdated: new Date(),
        messages: [
          {
            role: "system",
            content: systemPrompt,
            timestamp: new Date()
          },
          {
            role: "assistant",
            content: userPrompt,
            timestamp: new Date()
          }
        ]
      });
      return threadRef.id;
    } catch (error) {
      console.error("Error creating thread:", error);
      throw error;
    }
  },

  // Add message to thread
  async addMessageToThread(threadId, message) {
    try {
      const threadRef = doc(db, "threads", threadId);
      
      // Create a clean message object without any undefined values
      const messageToAdd = {
        role: message.role,
        content: message.content,
        timestamp: new Date()
      };
      
      // Only add hasImage if it's true
      if (message.hasImage === true) {
        messageToAdd.hasImage = true;
        
        // Only add imageName if it exists
        if (message.imageName) {
          messageToAdd.imageName = message.imageName;
        }
      }
      
      await updateDoc(threadRef, {
        messages: arrayUnion(messageToAdd),
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error("Error adding message to thread:", error);
      throw error;
    }
  },

  // Get all threads for a user
  async getUserThreads(userId) {
    try {
      const q = query(
        collection(db, "threads"),
        where("userId", "==", userId),
        orderBy("lastUpdated", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting user threads:", error);
      throw error;
    }
  },

  // Create a new thread with assistant's first message
  async createThreadWithAssistant(userId, title, assistantMessage) {
    try {
      const threadRef = await addDoc(collection(db, "threads"), {
        userId,
        title,
        createdAt: new Date(),
        lastUpdated: new Date(),
        messages: [
          {
            role: "assistant",
            content: assistantMessage,
            timestamp: new Date()
          }
        ]
      });
      return threadRef.id;
    } catch (error) {
      console.error("Error creating thread:", error);
      throw error;
    }
  },

  // Add new method to update thread document context
  async updateThreadDocumentContext(threadId, documentContext, documentName) {
    try {
      const threadRef = doc(db, "threads", threadId);
      await updateDoc(threadRef, {
        documentContext: documentContext,
        documentName: documentName,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error("Error updating thread document context:", error);
      throw error;
    }
  }
};
